import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

const EMPTY_FORM = {
  plan_code: '',
  version_code: '',
  price_monthly: '',
  annual_discount_pct: '10',
  api_rate_limit: '',
  api_src_limit: '',
  api_src_derived_flag: false,
  api_hist_limit: '',
  excel_plugin_flag: false,
  sso_custom_idp_flag: false,
  active_flag: true,
  default_flag: false,
};

const EMPTY_GRANDFATHER_FORM = {
  source_version_id: null,
  plan_code: '',
  source_version_code: '',
  grandfather_end_date: '',
  target_plan_version_id: '',
  clear: false,
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMoney = (cents) => {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatHistory = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  if (parsed === 999) return 'All Historic Data';
  if (parsed === 0) return 'Latest Year Only';
  if (parsed === 1) return '1 Year Back';
  return `${parsed} Years Back`;
};

const formatApiLimit = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  return Number(value).toLocaleString();
};

const formatSourceLimit = (value) => value || 'All Data Sources';

const parseMoneyInputToCents = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
};

const parseIntegerInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) ? numeric : null;
};

const parsePercentInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric * 100) / 100;
};

const DIFF_LABELS = {
  monthly_price: 'Monthly Price',
  annual_discount_pct: 'Annual Discount',
  annual_price: 'Annual Price',
  api_rate_limit: 'Monthly API Limit',
  api_src_limit: 'Data Sources',
  api_src_derived_flag: 'Derived Data',
  api_hist_limit: 'History Access',
  excel_plugin_flag: 'Excel Plugin',
  sso_custom_idp_flag: 'SSO / Custom IdP',
};

const formatDiffValue = (key, value) => {
  if (key === 'monthly_price' || key === 'annual_price') return formatMoney(value);
  if (key === 'annual_discount_pct') return value === null || value === undefined ? '—' : `${value}%`;
  if (key === 'api_rate_limit') return formatApiLimit(value);
  if (key === 'api_src_limit') return formatSourceLimit(value);
  if (key === 'api_src_derived_flag' || key === 'excel_plugin_flag' || key === 'sso_custom_idp_flag') return value ? 'Enabled' : 'Disabled';
  if (key === 'api_hist_limit') return formatHistory(value);
  return value ?? '—';
};

const normalizeDiffValue = (key, value) => {
  if (key === 'monthly_price' || key === 'annual_price') return value === null || value === undefined ? null : Number(value);
  if (key === 'annual_discount_pct') return value === null || value === undefined ? null : parsePercentInput(value);
  if (key === 'api_rate_limit' || key === 'api_hist_limit') return value === null || value === undefined ? null : Number(value);
  if (key === 'api_src_limit') return String(value || '').trim() || null;
  if (key === 'api_src_derived_flag' || key === 'excel_plugin_flag' || key === 'sso_custom_idp_flag') return Boolean(value);
  return value ?? null;
};

const getSuggestedVersionCode = (planCode, candidateVersions) => {
  if (!planCode) return '';
  const escapedPlanCode = planCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const versionPattern = new RegExp(`^${escapedPlanCode}_v(\\d+)$`, 'i');
  const nextVersionNumber = (candidateVersions || []).reduce((maxVersion, version) => {
    const match = String(version.version_code || '').match(versionPattern);
    if (!match) return maxVersion;
    return Math.max(maxVersion, Number(match[1]) || 0);
  }, 0) + 1;
  return `${planCode}_v${nextVersionNumber}`;
};

const CatalogColGroup = () => (
  <colgroup>
    <col style={{ width: '11%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '6%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '9%' }} />
    <col style={{ width: '6%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '5%' }} />
    <col style={{ width: '7%' }} />
    <col style={{ width: '5%' }} />
    <col style={{ width: '5%' }} />
    <col style={{ width: '7%' }} />
    <col style={{ width: '11%' }} />
    <col style={{ width: '15%' }} />
  </colgroup>
);

const DEFAULT_ROW_STYLE = {
  backgroundColor: 'rgba(13, 110, 253, 0.08)',
};

const TABLE_WRAPPER_STYLE = {
  overflowX: 'auto',
  overflowY: 'visible',
};

const FLOATING_MENU_STYLE = {
  position: 'fixed',
  zIndex: 1080,
  minWidth: '10rem',
};

const OpsSubscriptionCatalog = () => {
  const [plans, setPlans] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [actionLoading, setActionLoading] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [pendingSsoEnableVersion, setPendingSsoEnableVersion] = useState(null);
  const [showGrandfatherModal, setShowGrandfatherModal] = useState(false);
  const [grandfatherForm, setGrandfatherForm] = useState(EMPTY_GRANDFATHER_FORM);

  const loadCatalog = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/support/subscription-catalog');
      setPlans(response.data?.plans || []);
      setVersions(response.data?.versions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subscription catalog.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (!openActionMenu) return undefined;
    const handleWindowClick = () => setOpenActionMenu(null);
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpenActionMenu(null);
    };
    window.addEventListener('click', handleWindowClick);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [openActionMenu]);

  const versionsByPlan = useMemo(() => {
    const grouped = new Map();
    versions.forEach((version) => {
      const key = version.plan_code;
      const current = grouped.get(key) || [];
      current.push(version);
      grouped.set(key, current);
    });
    return grouped;
  }, [versions]);

  useEffect(() => {
    if (!plans.length) return;
    setForm((prev) => {
      const nextPlanCode = prev.plan_code || plans.find((plan) => plan.plan_code !== 'trial')?.plan_code || plans[0]?.plan_code || '';
      if (!nextPlanCode || nextPlanCode === prev.plan_code) return prev;
      const candidateVersions = versionsByPlan.get(nextPlanCode) || [];
      return {
        ...prev,
        plan_code: nextPlanCode,
        version_code: getSuggestedVersionCode(nextPlanCode, candidateVersions),
      };
    });
  }, [plans, versionsByPlan]);

  const currentDefaultVersion = useMemo(() => {
    if (!form.plan_code) return null;
    const candidateVersions = versionsByPlan.get(form.plan_code) || [];
    return candidateVersions.find((item) => item.default_flag) || candidateVersions[0] || null;
  }, [form.plan_code, versionsByPlan]);

  const pendingVersionValues = useMemo(() => {
    const isTrialPlan = form.plan_code === 'trial';
    const monthlyPriceCents = isTrialPlan ? 0 : parseMoneyInputToCents(form.price_monthly);
    const annualDiscountPct = isTrialPlan ? 0 : parsePercentInput(form.annual_discount_pct);
    const annualPriceCents = isTrialPlan || monthlyPriceCents === null || annualDiscountPct === null
      ? (isTrialPlan ? 0 : null)
      : Math.round(monthlyPriceCents * 12 * (1 - (annualDiscountPct / 100)));
    return {
      price_monthly_cents: monthlyPriceCents,
      annual_discount_pct: annualDiscountPct,
      price_annual_cents: annualPriceCents,
      api_rate_limit: parseIntegerInput(form.api_rate_limit),
      api_src_limit: String(form.api_src_limit || '').trim() || null,
      api_src_derived_flag: form.api_src_derived_flag === true,
      api_hist_limit: parseIntegerInput(form.api_hist_limit),
      excel_plugin_flag: form.excel_plugin_flag === true,
      sso_custom_idp_flag: form.sso_custom_idp_flag === true,
    };
  }, [form]);

  const createDiff = useMemo(() => {
    if (!currentDefaultVersion) return [];
    const fields = [
      ['monthly_price', currentDefaultVersion.price_monthly_cents, pendingVersionValues.price_monthly_cents],
      ['annual_discount_pct', currentDefaultVersion.annual_discount_pct, pendingVersionValues.annual_discount_pct],
      ['annual_price', currentDefaultVersion.price_annual_cents, pendingVersionValues.price_annual_cents],
      ['api_rate_limit', currentDefaultVersion.api_rate_limit, pendingVersionValues.api_rate_limit],
      ['api_src_limit', currentDefaultVersion.api_src_limit, pendingVersionValues.api_src_limit],
      ['api_src_derived_flag', Boolean(currentDefaultVersion.api_src_derived_flag), pendingVersionValues.api_src_derived_flag],
      ['api_hist_limit', currentDefaultVersion.api_hist_limit, pendingVersionValues.api_hist_limit],
      ['excel_plugin_flag', Boolean(currentDefaultVersion.excel_plugin_flag), pendingVersionValues.excel_plugin_flag],
      ['sso_custom_idp_flag', Boolean(currentDefaultVersion.sso_custom_idp_flag), pendingVersionValues.sso_custom_idp_flag],
    ];
    return fields
      .filter(([key, currentValue, nextValue]) => normalizeDiffValue(key, currentValue) !== normalizeDiffValue(key, nextValue))
      .map(([key, currentValue, nextValue]) => ({ key, currentValue, nextValue }));
  }, [currentDefaultVersion, pendingVersionValues]);

  useEffect(() => {
    if (!form.plan_code) return;
    const candidateVersions = versionsByPlan.get(form.plan_code) || [];
    const seed = candidateVersions.find((item) => item.default_flag) || candidateVersions[0] || null;
    if (!seed) return;
    setForm((prev) => {
      const nextVersionCode = prev.version_code || getSuggestedVersionCode(form.plan_code, candidateVersions);
      if (form.plan_code === 'trial') {
        return {
          ...prev,
          version_code: nextVersionCode,
          annual_discount_pct: '0',
          api_rate_limit: seed.api_rate_limit ?? '',
          api_src_limit: seed.api_src_limit ?? '',
          api_src_derived_flag: Boolean(seed.api_src_derived_flag),
          api_hist_limit: seed.api_hist_limit ?? '',
          excel_plugin_flag: Boolean(seed.excel_plugin_flag),
          sso_custom_idp_flag: Boolean(seed.sso_custom_idp_flag),
        };
      }
      return {
        ...prev,
        version_code: nextVersionCode,
        price_monthly: seed.price_monthly_cents != null ? (Number(seed.price_monthly_cents) / 100).toFixed(2) : '',
        annual_discount_pct: seed.annual_discount_pct != null ? String(seed.annual_discount_pct) : '10',
        api_rate_limit: seed.api_rate_limit ?? '',
        api_src_limit: seed.api_src_limit ?? '',
        api_src_derived_flag: Boolean(seed.api_src_derived_flag),
        api_hist_limit: seed.api_hist_limit ?? '',
        excel_plugin_flag: Boolean(seed.excel_plugin_flag),
        sso_custom_idp_flag: Boolean(seed.sso_custom_idp_flag),
      };
    });
  }, [form.plan_code, versionsByPlan]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm((prev) => ({ ...EMPTY_FORM, plan_code: prev.plan_code || '' }));
  };

  const handleCreate = async () => {
    setActionLoading('create');
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/support/subscription-catalog/versions', form);
      setStatus({ type: 'success', message: response.data?.message || 'Subscription plan version created.' });
      resetForm();
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create subscription plan version.');
    } finally {
      setActionLoading('');
    }
  };

  const handleCreateRequest = (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });
    setError('');
    if (!createDiff.length) {
      setError(currentDefaultVersion
        ? `No plan attributes differ from the current default version (${currentDefaultVersion.version_code}). Update at least one pricing or entitlement field before creating a new version.`
        : 'No plan attributes differ from the current default version.');
      return;
    }
    setShowCreateConfirm(true);
  };

  const handleAction = async (versionId, action) => {
    setActionLoading(`${action}-${versionId}`);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.post(`/acct-mgmt/v1/support/subscription-catalog/versions/${versionId}/${action}`);
      setStatus({ type: 'success', message: response.data?.message || 'Subscription plan version updated.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update subscription plan version.');
    } finally {
      setActionLoading('');
    }
  };

  const handleGrandfatherSubmit = async () => {
    if (!grandfatherForm.source_version_id) return;
    const versionId = grandfatherForm.source_version_id;
    const actionKey = `grandfather-${versionId}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.post(`/acct-mgmt/v1/support/subscription-catalog/versions/${versionId}/grandfather-sunset`, grandfatherForm.clear
        ? { clear: true }
        : {
            grandfather_end_date: grandfatherForm.grandfather_end_date,
            post_grandfather_plan_version_id: grandfatherForm.target_plan_version_id,
          });
      setShowGrandfatherModal(false);
      setGrandfatherForm(EMPTY_GRANDFATHER_FORM);
      setStatus({ type: 'success', message: response.data?.message || 'Grandfather sunset updated.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update grandfather sunset.');
    } finally {
      setActionLoading('');
    }
  };

  const openGrandfatherConfig = (version) => {
    const candidateVersions = (versionsByPlan.get(version.plan_code) || []).filter((item) => item.plan_version_id !== version.plan_version_id);
    const defaultTarget = candidateVersions.find((item) => item.default_flag) || candidateVersions[0] || null;
    const pendingTarget = version.grandfather_pending_target_plan_version_id
      ? candidateVersions.find((item) => String(item.plan_version_id) === String(version.grandfather_pending_target_plan_version_id))
      : null;
    const targetVersion = pendingTarget || defaultTarget;
    setGrandfatherForm({
      source_version_id: version.plan_version_id,
      plan_code: version.plan_code,
      source_version_code: version.version_code,
      grandfather_end_date: toDateInputValue(version.grandfather_pending_end_date),
      target_plan_version_id: targetVersion ? String(targetVersion.plan_version_id) : '',
      clear: false,
    });
    setShowGrandfatherModal(true);
  };

  const openVersionActionMenu = (event, versionId) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setOpenActionMenu((prev) => {
      if (prev?.versionId === versionId) return null;
      return {
        versionId,
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      };
    });
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Subscription Catalog</h2>
          <div className="text-muted">Manage plan versions, default offerings, and catalog availability for new subscriptions.</div>
        </div>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadCatalog} disabled={loading || Boolean(actionLoading)}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between w-100 mb-3 border-bottom pb-2">
            <h5 className="mb-0">Create Subscription Plan Version</h5>
          </div>
          <form onSubmit={handleCreateRequest}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Plan</label>
                <select
                  className="form-select"
                  value={form.plan_code}
                  onChange={(e) => {
                    const nextPlanCode = e.target.value;
                    const candidateVersions = versionsByPlan.get(nextPlanCode) || [];
                    setForm({
                      ...EMPTY_FORM,
                      plan_code: nextPlanCode,
                      version_code: getSuggestedVersionCode(nextPlanCode, candidateVersions),
                    });
                  }}
                  required
                >
                  <option value="">Select plan</option>
                  {plans.map((plan) => (
                    <option key={plan.plan_id} value={plan.plan_code}>{plan.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Version Code</label>
                <input
                  className="form-control"
                  value={form.version_code}
                  onChange={(e) => handleChange('version_code', e.target.value)}
                  placeholder="e.g. growth_v2"
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Monthly Price (USD)</label>
                <input
                  className="form-control"
                  value={form.plan_code === 'trial' ? '0.00' : form.price_monthly}
                  onChange={(e) => handleChange('price_monthly', e.target.value)}
                  disabled={form.plan_code === 'trial'}
                  placeholder="e.g. 1600.00"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Annual Discount %</label>
                <input
                  className="form-control"
                  value={form.plan_code === 'trial' ? '0' : form.annual_discount_pct}
                  onChange={(e) => handleChange('annual_discount_pct', e.target.value)}
                  disabled={form.plan_code === 'trial'}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Monthly API Limit</label>
                <input
                  className="form-control"
                  value={form.api_rate_limit}
                  onChange={(e) => handleChange('api_rate_limit', e.target.value)}
                  placeholder="Blank = unlimited"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Data Sources</label>
                <input
                  className="form-control"
                  value={form.api_src_limit}
                  onChange={(e) => handleChange('api_src_limit', e.target.value)}
                  placeholder="Blank = all data sources"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">History Access</label>
                <input
                  className="form-control"
                  value={form.api_hist_limit}
                  onChange={(e) => handleChange('api_hist_limit', e.target.value)}
                  placeholder="0, 1, 3, 999..."
                />
              </div>
              <div className="col-md-8 d-flex flex-wrap gap-4 align-items-center">
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="create-derived-flag"
                    checked={form.api_src_derived_flag}
                    onChange={(e) => handleChange('api_src_derived_flag', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="create-derived-flag">
                    Derived Data Enabled
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="create-excel-flag"
                    checked={form.excel_plugin_flag}
                    onChange={(e) => handleChange('excel_plugin_flag', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="create-excel-flag">
                    Excel Plugin Enabled
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="create-sso-flag"
                    checked={form.sso_custom_idp_flag}
                    onChange={(e) => handleChange('sso_custom_idp_flag', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="create-sso-flag">
                    SSO / Custom IdP Enabled
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="create-active-flag"
                    checked={form.active_flag}
                    onChange={(e) => handleChange('active_flag', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="create-active-flag">
                    Active
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="create-default-flag"
                    checked={form.default_flag}
                    onChange={(e) => handleChange('default_flag', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="create-default-flag">
                    Set as Default
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <button type="submit" className="btn btn-primary" disabled={actionLoading === 'create'}>
                {actionLoading === 'create' ? 'Creating...' : 'Create Version'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {loading ? (
        <div className="text-muted">Loading subscription catalog...</div>
      ) : (
        plans.map((plan) => {
          const planVersions = [...(versionsByPlan.get(plan.plan_code) || [])].sort((a, b) => {
            const aStart = a.effective_start ? new Date(a.effective_start).getTime() : 0;
            const bStart = b.effective_start ? new Date(b.effective_start).getTime() : 0;
            if (aStart !== bStart) return aStart - bStart;
            return Number(a.plan_version_id) - Number(b.plan_version_id);
          });
          return (
            <div className="card mb-4" key={plan.plan_id}>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between w-100 mb-3 border-bottom pb-2">
                  <div>
                    <h5 className="mb-0">{plan.name}</h5>
                    <div className="text-muted small">{plan.plan_code}</div>
                  </div>
                  <span className={`badge ${plan.active_flag ? 'bg-success' : 'bg-secondary'}`}>
                    {plan.active_flag ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {planVersions.length === 0 ? (
                  <div className="text-muted">No plan versions found.</div>
                ) : (
                  <div className="table-responsive" style={TABLE_WRAPPER_STYLE}>
                    <table className="table table-sm align-middle mb-0">
                      <CatalogColGroup />
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Monthly</th>
                          <th>Annual</th>
                          <th>Discount</th>
                          <th>API Limit</th>
                          <th>Sources</th>
                          <th>Derived</th>
                          <th>History</th>
                          <th>Excel</th>
                          <th>SSO</th>
                          <th>Current Orgs</th>
                          <th>Scheduled Orgs</th>
                          <th>State</th>
                          <th>Effective Start</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planVersions.map((version) => {
                          const pendingSunsetCount = Number(version.grandfather_pending_org_count || 0);
                          const hasSunsetPolicy = Boolean(version.grandfather_sunset_policy_id) || pendingSunsetCount > 0;
                          const canEditGrandfatherSunset = !hasSunsetPolicy || version.grandfather_pending_editable_flag !== false;
                          const grandfatherActionLabel = hasSunsetPolicy ? 'Edit Grandfather Sunset' : 'Configure Grandfather Sunset';
                          const grandfatherMissingCount = Number(version.grandfather_missing_org_count || 0);
                          const grandfatherRemovedCount = Number(version.grandfather_removed_org_count || 0);
                          const grandfatherAppliedCount = Number(version.grandfather_applied_org_count || 0);
                          const grandfatherMismatchedCount = Number(version.grandfather_mismatched_org_count || 0);
                          const sourceRowCellStyle = {
                            ...(version.default_flag ? DEFAULT_ROW_STYLE : {}),
                            ...(hasSunsetPolicy ? { borderBottom: 0 } : {}),
                          };
                          const sunsetRowCellStyle = {
                            ...(version.default_flag ? DEFAULT_ROW_STYLE : {}),
                            borderTop: 0,
                            paddingTop: 0,
                            paddingBottom: '0.75rem',
                          };
                          return (
                            <React.Fragment key={version.plan_version_id}>
                              <tr>
                                <td style={sourceRowCellStyle}>
                                  <div className="fw-semibold">{version.version_code}</div>
                                  {version.default_flag && <div className="mt-1"><span className="badge bg-primary">Default</span></div>}
                                </td>
                                <td style={sourceRowCellStyle}>{formatMoney(version.price_monthly_cents)}</td>
                                <td style={sourceRowCellStyle}>{formatMoney(version.price_annual_cents)}</td>
                                <td style={sourceRowCellStyle}>{version.annual_discount_pct != null ? `${version.annual_discount_pct}%` : '—'}</td>
                                <td style={sourceRowCellStyle}>{formatApiLimit(version.api_rate_limit)}</td>
                                <td style={sourceRowCellStyle}>{formatSourceLimit(version.api_src_limit)}</td>
                                <td style={sourceRowCellStyle}>{version.api_src_derived_flag ? 'Yes' : 'No'}</td>
                                <td style={sourceRowCellStyle}>{formatHistory(version.api_hist_limit)}</td>
                                <td style={sourceRowCellStyle}>{version.excel_plugin_flag ? 'Yes' : 'No'}</td>
                                <td style={sourceRowCellStyle}>{version.sso_custom_idp_flag ? 'Yes' : 'No'}</td>
                                <td style={sourceRowCellStyle}>{version.current_org_count}</td>
                                <td style={sourceRowCellStyle}>{version.scheduled_org_count}</td>
                                <td style={sourceRowCellStyle}>
                                  <div>{version.version_active ? 'Active' : 'Inactive'}</div>
                                  <div className="small text-muted">{version.default_flag ? 'Default' : 'Non-default'}</div>
                                  {hasSunsetPolicy && (
                                    <div className="small text-muted">{pendingSunsetCount} pending sunset</div>
                                  )}
                                </td>
                                <td style={sourceRowCellStyle}>{formatDateTime(version.effective_start)}</td>
                                <td style={sourceRowCellStyle}>
                                  <div className="position-relative">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                      onClick={(event) => openVersionActionMenu(event, version.plan_version_id)}
                                      disabled={Boolean(actionLoading)}
                                    >
                                      Actions
                                    </button>
                                    {openActionMenu?.versionId === version.plan_version_id && (
                                      <div
                                        className="dropdown-menu show"
                                        style={{
                                          ...FLOATING_MENU_STYLE,
                                          top: `${openActionMenu.top}px`,
                                          right: `${openActionMenu.right}px`,
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        {!version.default_flag && version.version_active && (
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={async () => {
                                              setOpenActionMenu(null);
                                              await handleAction(version.plan_version_id, 'default');
                                            }}
                                          >
                                            {actionLoading === `default-${version.plan_version_id}` ? 'Saving...' : 'Set Default'}
                                          </button>
                                        )}
                                        {(Number(version.grandfather_eligible_org_count || 0) > 0 || hasSunsetPolicy) && (
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => {
                                              if (!canEditGrandfatherSunset) return;
                                              setOpenActionMenu(null);
                                              openGrandfatherConfig(version);
                                            }}
                                            disabled={!canEditGrandfatherSunset}
                                          >
                                            {canEditGrandfatherSunset ? grandfatherActionLabel : 'Grandfather Sunset Locked'}
                                          </button>
                                        )}
                                        {!version.sso_custom_idp_flag && (
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => {
                                              setOpenActionMenu(null);
                                              setPendingSsoEnableVersion(version);
                                            }}
                                          >
                                            {actionLoading === `sso-custom-idp/enable-${version.plan_version_id}` ? 'Saving...' : 'Enable SSO / Custom IdP'}
                                          </button>
                                        )}
                                        {version.version_active ? (
                                          <button
                                            type="button"
                                            className="dropdown-item text-danger"
                                            onClick={async () => {
                                              setOpenActionMenu(null);
                                              await handleAction(version.plan_version_id, 'deactivate');
                                            }}
                                            disabled={version.default_flag}
                                          >
                                            {actionLoading === `deactivate-${version.plan_version_id}` ? 'Updating...' : 'Deactivate'}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="dropdown-item text-success"
                                            onClick={async () => {
                                              setOpenActionMenu(null);
                                              await handleAction(version.plan_version_id, 'activate');
                                            }}
                                          >
                                            {actionLoading === `activate-${version.plan_version_id}` ? 'Updating...' : 'Activate'}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {hasSunsetPolicy && (
                                <tr>
                                  <td colSpan="14" style={sunsetRowCellStyle}>
                                    <div className="d-flex flex-wrap align-items-center gap-3 small px-2 py-2 rounded border-start border-3 border-info bg-light">
                                      <span className="fw-semibold">Grandfather sunset</span>
                                      <span><span className="text-muted">Pending:</span> {pendingSunsetCount}</span>
                                      {grandfatherAppliedCount > 0 && <span><span className="text-muted">Applied:</span> {grandfatherAppliedCount}</span>}
                                      {grandfatherRemovedCount > 0 && <span><span className="text-muted">Removed:</span> {grandfatherRemovedCount}</span>}
                                      {grandfatherMissingCount > 0 && <span className="text-warning"><span className="text-muted">Missing:</span> {grandfatherMissingCount}</span>}
                                      {grandfatherMismatchedCount > 0 && <span className="text-danger"><span className="text-muted">Mismatched:</span> {grandfatherMismatchedCount}</span>}
                                      <span><span className="text-muted">End date:</span> {version.grandfather_pending_end_date ? formatDateOnly(version.grandfather_pending_end_date) : 'Mixed dates'}</span>
                                      <span><span className="text-muted">Replacement:</span> {version.grandfather_pending_target_version_code || 'Mixed targets'}</span>
                                      <span className={`badge ${canEditGrandfatherSunset ? 'bg-info text-dark' : 'bg-secondary'}`}>
                                        {canEditGrandfatherSunset ? 'Editable' : 'Locked'}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
      {showCreateConfirm && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Subscription Plan Version</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowCreateConfirm(false)}
                    disabled={Boolean(actionLoading)}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3 text-muted">
                    Review the changes from the current default version <span className="fw-semibold">{currentDefaultVersion?.version_code || '—'}</span> to the new version <span className="fw-semibold">{form.version_code || '—'}</span>.
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Current Default</th>
                          <th>New Version</th>
                        </tr>
                      </thead>
                      <tbody>
                        {createDiff.map((item) => (
                          <tr key={item.key}>
                            <td className="fw-semibold">{DIFF_LABELS[item.key] || item.key}</td>
                            <td>{formatDiffValue(item.key, item.currentValue)}</td>
                            <td>{formatDiffValue(item.key, item.nextValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateConfirm(false)}
                    disabled={Boolean(actionLoading)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      setShowCreateConfirm(false);
                      await handleCreate();
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    {actionLoading === 'create' ? 'Creating...' : 'Create Version'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {pendingSsoEnableVersion && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Enable SSO / Custom IdP?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setPendingSsoEnableVersion(null)}
                    disabled={Boolean(actionLoading)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    This will enable SSO / Custom IdP for <span className="fw-semibold">{pendingSsoEnableVersion.version_code}</span>.
                  </p>
                  <div className="alert alert-warning mb-0" role="alert">
                    This is a one-way catalog change. After SSO / Custom IdP is enabled for this plan version, it cannot be undone from the ops portal.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPendingSsoEnableVersion(null)}
                    disabled={Boolean(actionLoading)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={async () => {
                      const version = pendingSsoEnableVersion;
                      setPendingSsoEnableVersion(null);
                      await handleAction(version.plan_version_id, 'sso-custom-idp/enable');
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    {actionLoading === `sso-custom-idp/enable-${pendingSsoEnableVersion.plan_version_id}` ? 'Saving...' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {showGrandfatherModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {Number((versions.find((item) => item.plan_version_id === grandfatherForm.source_version_id) || {})?.grandfather_pending_org_count || 0) > 0
                      ? 'Edit Grandfather Sunset'
                      : 'Configure Grandfather Sunset'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowGrandfatherModal(false)}
                    disabled={Boolean(actionLoading)}
                  />
                </div>
                <div className="modal-body">
                  {(() => {
                    const sourceVersion = versions.find((item) => item.plan_version_id === grandfatherForm.source_version_id) || null;
                    const targetOptions = (versionsByPlan.get(grandfatherForm.plan_code) || []).filter((item) => item.plan_version_id !== grandfatherForm.source_version_id);
                    return (
                      <>
                        <div className="text-muted mb-3">
                          Apply a pricing sunset to active subscribers currently on <span className="fw-semibold">{grandfatherForm.source_version_code || '—'}</span>. The change will apply at the first renewal on or after the selected grandfather end date.
                        </div>
                        <div className="mb-3">
                          <div className="small text-muted">Eligible Active Subscriptions</div>
                          <div className="fw-semibold">{sourceVersion?.grandfather_eligible_org_count ?? 0}</div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Grandfather End Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={grandfatherForm.grandfather_end_date}
                            onChange={(e) => setGrandfatherForm((prev) => ({ ...prev, grandfather_end_date: e.target.value, clear: false }))}
                            disabled={grandfatherForm.clear}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Post-Grandfather Target Version</label>
                          <select
                            className="form-select"
                            value={grandfatherForm.target_plan_version_id}
                            onChange={(e) => setGrandfatherForm((prev) => ({ ...prev, target_plan_version_id: e.target.value, clear: false }))}
                            disabled={grandfatherForm.clear}
                          >
                            <option value="">Select target version</option>
                            {targetOptions.map((option) => (
                              <option key={option.plan_version_id} value={option.plan_version_id}>
                                {option.version_code}{option.default_flag ? ' (Default)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {Number(sourceVersion?.grandfather_pending_org_count || 0) > 0 && (
                          <div className="d-flex align-items-start gap-2 mt-3">
                            <input
                              className="form-check-input mt-1 flex-shrink-0"
                              type="checkbox"
                              id="grandfather-clear-flag"
                              checked={grandfatherForm.clear}
                              onChange={(e) => setGrandfatherForm((prev) => ({ ...prev, clear: e.target.checked }))}
                            />
                            <label className="form-check-label mb-0" htmlFor="grandfather-clear-flag">
                              Clear existing grandfather sunset configuration for active subscribers on this version
                            </label>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowGrandfatherModal(false)}
                    disabled={Boolean(actionLoading)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn ${grandfatherForm.clear ? 'btn-outline-danger' : 'btn-primary'}`}
                    onClick={handleGrandfatherSubmit}
                    disabled={Boolean(actionLoading) || (!grandfatherForm.clear && (!grandfatherForm.grandfather_end_date || !grandfatherForm.target_plan_version_id))}
                  >
                    {actionLoading === `grandfather-${grandfatherForm.source_version_id}`
                      ? 'Saving...'
                      : grandfatherForm.clear ? 'Clear Sunset' : 'Save Sunset'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default OpsSubscriptionCatalog;
